import React from 'react';
import { Icon, IconEnum } from '@investoro/core';
import styled from 'styled-components';
import type { LocationSectionDto } from 'utils/types/AiPredictorModels';
import { PoiIconTypeEnum } from 'utils/types/AiPredictorModels';

import { SectionCard, SectionHeader, SectionTitle, TextContent } from 'components/valuation/common';

const travelIconMap = {
    car: '🚗',
    transit: '🚌'
};

const poiIconMap: Record<PoiIconTypeEnum, string> = {
    [PoiIconTypeEnum.Centrum]: '🏙️',
    [PoiIconTypeEnum.Przedszkole]: '🧒',
    [PoiIconTypeEnum.Szkola]: '🏫',
    [PoiIconTypeEnum.Uczelnia]: '🎓',
    [PoiIconTypeEnum.Supermarket]: '🛒',
    [PoiIconTypeEnum.GaleriaHandlowa]: '🏬',
    [PoiIconTypeEnum.PrzystanekAutobusowy]: '🚌',
    [PoiIconTypeEnum.PrzystanekTramwajowy]: '🚊',
    [PoiIconTypeEnum.StacjaKolejowa]: '🚉',
    [PoiIconTypeEnum.ToryKolejowe]: '🛤️',
    [PoiIconTypeEnum.Autostrada]: '🛣️',
    [PoiIconTypeEnum.Lotnisko]: '✈️',
    [PoiIconTypeEnum.Woda]: '💧',
    [PoiIconTypeEnum.Szpital]: '🏥',
    [PoiIconTypeEnum.Przychodnia]: '🏥',
    [PoiIconTypeEnum.Park]: '🌳',
    [PoiIconTypeEnum.Las]: '🌲',
    [PoiIconTypeEnum.Cmentarz]: '⛪',
    [PoiIconTypeEnum.StrefaPrzemyslowa]: '🏭',
    [PoiIconTypeEnum.LiniaEnergetyczna]: '⚡',
    [PoiIconTypeEnum.StrazPozarna]: '🚒',
    [PoiIconTypeEnum.WysypiskoSmieci]: '🗑️'
};

const LocationGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 100px;
    align-items: start;

    @media (max-width: 600px) {
        grid-template-columns: 1fr;
        gap: 40px;
    }
`;

const ColumnLabel = styled.div`
    font-size: var(--font-size-body-3);
    font-weight: 600;
    color: var(--color-primary-70);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 8px;
`;

const PoiGrid = styled.div`
    display: flex;
    flex-direction: column;
`;

const PoiChip = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 7px 0;
    border-bottom: 1px solid var(--color-alt-second);

    &:last-child {
        border-bottom: none;
    }
`;

const PoiEmoji = styled.span`
    font-size: 16px;
    line-height: 1;
    flex-shrink: 0;
    width: 20px;
    text-align: center;
`;

const PoiInfo = styled.div`
    display: flex;
    align-items: baseline;
    gap: 6px;
    flex: 1;
`;

const PoiName = styled.span`
    font-size: var(--font-size-body-3);
    font-weight: 600;
    color: var(--color-primary);
`;

const PoiDistance = styled.span`
    font-size: var(--font-size-body-3);
    font-weight: 500;
    color: var(--color-alt);
    margin-left: auto;
`;

const TravelList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const TravelItem = styled.div`
    display: flex;
    align-items: center;
    padding: 7px 0;
    border-bottom: 1px solid var(--color-alt-second);

    &:last-child {
        border-bottom: none;
    }
`;

const TravelDest = styled.span`
    font-size: var(--font-size-body-2);
    font-weight: 600;
    color: var(--color-primary);
`;

const TravelTimes = styled.div`
    display: flex;
    gap: 8px;
    margin-left: auto;
`;

const TravelTimeChip = styled.span`
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: var(--font-size-body-3);
    color: var(--color-primary-70);
    font-weight: 500;
    min-width: 70px;
    justify-content: flex-start;
`;

interface ValuationLocationProps {
    section: LocationSectionDto;
}

const ValuationLocation = ({ section }: ValuationLocationProps) => {
    const hasPois = section.highlightedPois && section.highlightedPois.length > 0;
    const hasTravelTimes = section.travelTimes && section.travelTimes.length > 0;

    if (!hasPois && !hasTravelTimes) return null;

    return (
        <SectionCard>
            <SectionHeader>
                <Icon icon={IconEnum.MAP_PIN} />
                <SectionTitle>Lokalizacja i atrakcyjność</SectionTitle>
            </SectionHeader>
            {section.sectionDescription &&
                <TextContent>{section.sectionDescription}</TextContent>
            }
            <LocationGrid>
                {hasPois &&
                    <div>
                        <ColumnLabel>W pobliżu</ColumnLabel>
                        <PoiGrid>
                            {section.highlightedPois?.map((poi) => {
                                const emoji = poi.iconType ? poiIconMap[poi.iconType] ?? '📍' : '📍';

                                return (
                                    <PoiChip key={`${poi.name}-${poi.distance}`}>
                                        <PoiEmoji>{emoji}</PoiEmoji>
                                        <PoiInfo>
                                            <PoiName>{poi.name}</PoiName>
                                            <PoiDistance>{poi.distance}</PoiDistance>
                                        </PoiInfo>
                                    </PoiChip>
                                );
                            })}
                        </PoiGrid>
                    </div>
                }
                {hasTravelTimes &&
                    <div>
                        <ColumnLabel>Czasy dojazdu</ColumnLabel>
                        <TravelList>
                            {section.travelTimes?.map((element) => <TravelItem key={element.destination}>
                                <TravelDest>{element.destination}</TravelDest>
                                <TravelTimes>
                                    {element.carTime && <TravelTimeChip>{travelIconMap.car} {element.carTime}</TravelTimeChip>}
                                    {element.transitTime && <TravelTimeChip>{travelIconMap.transit} {element.transitTime}</TravelTimeChip>}
                                </TravelTimes>
                            </TravelItem>)}
                        </TravelList>
                    </div>
                }
            </LocationGrid>
        </SectionCard>
    );
};

export default ValuationLocation;
